import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';

import {
	Icon,
	IconBus,
	IconCopy,
	IconMaintenance,
	IconTrain,
} from '@flixbus/honeycomb-icons-react';
import { Box, Divider, Link as HCLink, Tag } from '@flixbus/honeycomb-react';

import Deliniator from 'common/Deliniator';
import { handleCopyRideId } from 'pages/RideDetails/utils/copyToClipboard';
import SequenceTags from 'pages/RideListPage/components/SequenceTags';
import VehicleAssignmentTag from 'pages/RideListPage/components/VehicleAssignmentTag';
import { NA_LABEL } from 'pages/RideListPage/const';
import { BREAKDOWN_REASON_LABELS } from 'utils/breakdown';
import { getOneOpsRideUrl, isProduction } from 'utils/getRunPlanningUrl';
import { buildSegments } from 'utils/updateMapper';

import { buildRideItemData } from './buildRideItemData';
import { RIDE_ITEM_TEXT } from './texts';

import styles from './styles.module.css';

const renderVehicleTags = (vehiclePlan) => {
	if (vehiclePlan.mode === 'na') {
		return NA_LABEL;
	}
	if (vehiclePlan.mode === 'single-no-bus') {
		return (
			<VehicleAssignmentTag
				vehicleAssignment={vehiclePlan.singleAssignment}
				icon={IconBus}
				fallbackLabel={NA_LABEL}
			/>
		);
	}
	return vehiclePlan.entries.map(({ busNumber, assignment, isBusType }, idx) => (
		<VehicleAssignmentTag
			key={busNumber || idx}
			vehicleAssignment={assignment}
			icon={isBusType ? IconBus : IconTrain}
			fallbackLabel={NA_LABEL}
		/>
	));
};

const RideItem = ({ rideDetails, timezone }) => {
	if (!rideDetails.stations || rideDetails.stations.length === 0) return null;

	const {
		lineLabel,
		tripLabel,
		runLabel,
		extraRuns,
		jobLabel,
		isRunsJobsDataEmpty,
		routeLabel,
		departureLabel,
		timezoneSelected,
		detailsPath,
		vehiclePlan,
	} = buildRideItemData({ rideDetails, timezone });

	const segments = buildSegments(rideDetails.stations, rideDetails.workAssignmentSequences);

	const breakdowns = rideDetails.workAssignmentSequences
		.filter((seq) => seq.breakdown)
		.map((seq) => seq.breakdown);

	const runsJobsStyles = classNames({
		[styles.bottomTripContent]: true,
		[styles.stub]: isRunsJobsDataEmpty,
	});

	return (
		<Box extraClasses={styles.item}>
			{/* LEFT PANEL: Line code + trip info */}
			<div className={styles.tripInfoWrapper}>
				<div className={styles.topTripContent}>
					<div className={styles.lineCode}>{lineLabel}</div>
					<div className={styles.tripNumber}>{tripLabel}</div>
				</div>
				<div className={styles.tripDivider}>
					<Divider />
				</div>

				<div className={runsJobsStyles}>
					<div className={styles.subInfo}>
						{runLabel}
						{extraRuns > 0 && (
							<span className={styles.extraRuns}>{RIDE_ITEM_TEXT.extraRuns(extraRuns)}</span>
						)}
					</div>
					<div className={styles.subInfo}>{jobLabel}</div>
				</div>
			</div>

			{/* CENTER: Route and Departure */}
			<div className={styles.center}>
				<Link to={detailsPath} className={styles.link} target="_blank">
					<div className={styles.stationsInfo}>
						<div className={styles.stationsContentLeft}>
							<div className={styles.route}>{routeLabel}</div>
							<div className={styles.departure}>
								{departureLabel}
								{breakdowns.map((bd, idx) => {
									const label = BREAKDOWN_REASON_LABELS[bd.reason];
									if (!label) return null;
									return (
										<Tag key={idx} small appearance="danger" display="subtle">
											<Icon InlineIcon={IconMaintenance} size={3} />
											{label}
										</Tag>
									);
								})}
							</div>
						</div>
						<div className={styles.stationsContentRight}>
							<Deliniator />
							<div className={styles.stationsContentRightWrapper}>
								<div className={styles.metaItem}>
									<strong className={styles.rideId}>{RIDE_ITEM_TEXT.rideIdLabel}</strong>
									{isProduction() ? (
										<HCLink href={getOneOpsRideUrl(rideDetails.id)} target="_blank">
											{rideDetails.id}
										</HCLink>
									) : (
										<span>{rideDetails.id}</span>
									)}
									<Icon
										extraClasses={styles.copyIcon}
										InlineIcon={IconCopy}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleCopyRideId(rideDetails.id);
										}}
									/>
								</div>
								<div className={styles.metaItem}>
									<strong className={styles.vehicle}>{RIDE_ITEM_TEXT.vehiclesLabel}</strong>
									{renderVehicleTags(vehiclePlan)}
								</div>
							</div>
						</div>
					</div>
				</Link>

				<SequenceTags
					timezone={timezoneSelected}
					rideId={rideDetails.id}
					line={rideDetails.lineCode}
					tripNumber={rideDetails.tripNumber}
					departure={rideDetails.departure}
					segments={segments}
				/>
			</div>
		</Box>
	);
};

export default RideItem;
